/**
 * Provides user-, client-, and component-specific privileges.
 *
 * ```js
 * // esm
 * import { security } from '@paychex/internal';
 *
 * // cjs
 * const { security } = require('@paychex/internal');
 *
 * // iife
 * const { security } = window['@paychex/internal'];
 *
 * // amd
 * require(['@paychex/internal'], function({ security }) { ... });
 * define(['@paychex/internal'], function({ security }) { ... });
 * ```
 *
 * @module security
 * @example
 * ```js
 * const userAccess = security.create();
 *
 * userAccess.load(async function getEntitlements() {
 *   // load user access / advanced security for
 *   // a user and/or client(s); be sure to return
 *   // data in the format the security API expects
 *   return userAccess.from.v2(response.data);
 * });
 *
 * // retrieve permissions by caid and component guid:
 * const perms = await userAccess.permissions(caid, guid);
 * console.log(perms); // { visible: ..., enabled: ..., ... }
 *
 * // you can partially apply the permissions function with
 * // a particular client to make repeat calls clearer:
 * const perms = userAccess.permissions(caid);
 * await perms(guid);
 * await perms(anotherGuid);
 *
 * // you can also combine permissions together so that
 * // the resulting privileges are only allowed when all
 * // permissions permit it, OR are allowed when any
 * // permission permits it:
 *
 * const perm1 = await perms(guid1);
 * const perm2 = await perms(guid2);
 * const perm3 = await perms(guid3);
 *
 * const any = userAccess.permissive(perm1, perm2, perm3); // ORs permissions
 * const all = userAccess.restrictive(perm1, perm2, perm3); // ANDs permissions
 *
 * if (any.visible) { ... }
 * else if (all.enabled) { ... }
 * ```
 */

import { errors, signals } from '@paychex/core';
import { has, set, curry, CurriedFunction2, flatten, isEmpty } from 'lodash';

enum Active {
    OFF = '0',
    ON = '1',
}

/** A unique security component GUID. */
export type Guid = string;

/** The unique client identifier. */
export type Caid = string;

/** A string of 0s and 1s used to indicate 1 or more permission sets. */
export type Bitmap = string;

/** A combination of client id and user access version used to look up associated security bitmaps. */
export type CaidVersion = string;

/** The offset into a security bitmap at which to grab the set of permissions. */
export type BitmapOffset = number;

/** The version of user access privileges being returned. */
export type SecurityVersion = number;

/** The permission that is either allowed or denied. */
export type PermissionLevel = keyof Permissions;

/** Async function that returns {@link SecurityInfo} to the {@link load} method. */
export interface SecurityLoader {
    (): Promise<SecurityInfo>
}

/** Represents the data needed by the security API to look up permissions by client id and component guid. */
export interface SecurityInfo {
    masks: PermissionLevel[],
    bitmaps: Record<CaidVersion, Bitmap>,
    components: Record<Guid, [SecurityVersion, BitmapOffset]>,
}

/** The permissions associated with a given client and security component guid. */
export interface Permissions {
    /** Whether the component should be shown. */
    visible: boolean,
    /** Whether the component should be interactive or editable. */
    enabled: boolean,
    /** Whether the user can perform an action on the component. */
    perform: boolean,
    /** Whether the component value should be fully obfuscated or masked. */
    masked: boolean,
    /** Whether the component value should be partially obfuscated or masked. */
    partialMasked: boolean,
    /** Whether the component is fully interactive or editable. */
    enabledFull: boolean,
}

/**
 * Methods used to get security information for secured components by client id.
 *
 * @example
 * ```js
 * const userAccess = security.create();
 *
 * userAccess.load(async function getEntitlements() {
 *   // load user access / advanced security for
 *   // a user and/or client(s); be sure to return
 *   // data in the format the security API expects
 *   return userAccess.from.v2(response.data);
 * });
 *
 * // retrieve permissions by caid and component guid:
 * const perms = await userAccess.permissions(caid, guid);
 * console.log(perms); // { visible: ..., enabled: ..., ... }
 *
 * // you can partially apply the permissions function with
 * // a particular client to make repeat calls clearer:
 * const perms = userAccess.permissions(caid);
 * await perms(guid);
 * await perms(anotherGuid);
 *
 * // you can also combine permissions together so that
 * // the resulting privileges are only allowed when all
 * // permissions permit it, OR are allowed when any
 * // permission permits it:
 *
 * const perm1 = await perms(guid1);
 * const perm2 = await perms(guid2);
 * const perm3 = await perms(guid3);
 *
 * const any = userAccess.permissive(perm1, perm2, perm3); // ORs permissions
 * const all = userAccess.restrictive(perm1, perm2, perm3); // ANDs permissions
 *
 * if (any.visible) { ... }
 * else if (all.enabled) { ... }
 * ```
 */
export interface SecurityAPI {

    /**
     * Instance of a {@link Permissions} object with all privileges enabled.
     *
     * @example
     * ```js
     * const userAccess = security.create();
     *
     * if (someCondition)
     *   return userAccess.ALL;
     * ```
     */
    ALL: Readonly<typeof ALL>,

    /**
     * Instance of a {@link Permissions} object with no privileges enabled.
     *
     * @example
     * ```js
     * const userAccess = security.create();
     *
     * if (someCondition)
     *   return userAccess.NONE;
     * ```
     */
    NONE: Readonly<typeof NONE>,

    /**
     * Registers an asynchronous function that returns {@link SecurityInfo} used by
     * {@link permissions} to retrieve privileges for a requested component and client.
     * If the async function throws an Error or returns a rejected Promise, that Error
     * or rejection reason will be propagated back to the caller. If called multiple
     * times in a row, the earlier calls will be aborted, and an Error will be thrown.
     *
     * @example
     * ```js
     * bus.on('client-changed', (caid) => {
     *   return security.load(async function getEntitlements() {
     *     // load SecurityInfo for the requested client;
     *     // if another client is chosen in the meantime
     *     // then earlier calls to load will be aborted
     *   }).catch(function(err) {
     *     // optionally, handle errors due to aborts or
     *     // to the getEntitlements function failing
     *   });
     * });
     * ```
     */
    load: (loader: SecurityLoader) => Promise<void>,

    /**
     * Retrieves a {@link Permissions} instance for the requested client
     * id and component GUID. This method waits for any in-progress calls
     * to {@link load} to settle.
     *
     * If the initial load call or most recent load call fails then calls
     * to this function will return the {@link NONE} permission. In other
     * words, the most recent load call must have succeeded in order to
     * retrieve correct permissions. You should handle any failures in the
     * {@link load} method accordingly.
     *
     * **NOTE:** This function is curried, so you can invoke it either with
     * caid and guid at the same time, or with just the caid and then later
     * with a component guid. See the examples for details.
     *
     * @param caid The unique client ID whose component permissions you wish to retrieve.
     * @param guid The secured component GUID whos permissions you wish to retrieve.
     * @example
     * ```js
     * const perms = await security.permissions(caid, guid);
     *
     * if (perms.enabled || perms.fullEnabled) { ... }
     * else if (perms.masked || perms.partialMasked) { ... }
     * else if (perms.visible) { ... }
     * ```
     * @example
     * ```js
     * // partially apply a client id to make repeat calls easier:
     * const perms = security.permissions(caid);
     *
     * const [perm1, perm2, perm3] = await Promise.all([
     *   perms(guid1),
     *   perms(guid2),
     *   perms(guid3),
     * ]);
     * ```
     */
    permissions: CurriedFunction2<Caid, Guid, Promise<Permissions>>,

    /**
     * Returns a {@link Permissions} instance that represents the least
     * restrictive combination of all the given Permissions. In other
     * words, if _any_ of the supplied permissions have `enabled` set to
     * true, then the resulting permission's `enabled` will be true.
     *
     * **NOTE:** If no permissions are provided, the result will be an
     * empty permission set matching {@link NONE}. In other words, the
     * most permissive result of no restrictions is having no permissions.
     *
     * @param permissions Zero or more {@link Permissions} instances to
     * convert into the least restrictive combination.
     * @example
     * ```js
     * const perm1 = await security.permissions(caid, guid1);
     * const perm2 = await security.permissions(caid, guid2);
     *
     * const atLeastOne = security.permissive(perm1, perm2);
     *
     * if (atLeastOne.enabled) { ... } // perm1.enabled or perm2.enabled
     * ```
     * @example
     * ```js
     * // passing no permissions returns the
     * // equivalent of security.NONE (no permissions)
     * const none = security.permissive();
     * none.enabled || none.visible; // all permissions will be false
     * ```
     */
    permissive: (...permissions: Permissions[]) => Permissions,

    /**
     * Returns a {@link Permissions} instance that represents the most
     * restrictive combination of all the given Permissions. In other
     * words, if _any_ of the supplied permissions have `enabled` set to
     * false, then the resulting permission's `enabled` will be false.
     *
     * **NOTE:** If no permissions are provided, the result will be an
     * empty permission set matching {@link ALL}. In other words, the
     * most restrictive result of no restrictions is having all permissions.
     *
     * @param permissions Zero or more {@link Permissions} instances to
     * convert into the most restrictive combination.
     * @example
     * ```js
     * const perm1 = await security.permissions(caid, guid1);
     * const perm2 = await security.permissions(caid, guid2);
     *
     * const all = security.restrictive(perm1, perm2);
     *
     * if (all.enabled) { ... } // perm1.enabled and perm2.enabled
     * ```
     * @example
     * ```js
     * // passing no permissions returns the
     * // equivalent of security.ALL (all permissions)
     * const all = security.permissive();
     * all.enabled || all.visible; // all permissions will be true
     * ```
     */
    restrictive: (...permissions: Permissions[]) => Permissions,

    from: {

        /**
         * Converts the legacy advanced security data object into
         * the modern user access format that enables faster lookups.
         *
         * @param info The legacy advanced security payload to convert
         * into the more modern and lookup-optimized user access format.
         * @example
         * ```js
         * security.load(async getLegacyEntitlements() {
         *   // make a data call to a legacy V2 advanced security
         *   // endpoint and convert the response to the new format
         *   const request = data.createRequest(legacyOperation);
         *   const response = await data.remote(request);
         *   return security.from.v2(response.data);
         * });
         * ```
         */
        v2: (info: V2SecurityResponse) => SecurityInfo

    }

}

/** Source of permissions data for a given client. */
export interface SecurityContext {
    caid: string,
    version: 'V1' | 'V2',
    bitmapData: Bitmap,
}

/** Identifies where in the associated {@link SecurityContext} to retrieve the component's permissions bitmap subset. */
export interface SecurityComponent {
    guid: string,
    offset: string,
    version: 'V1' | 'V2',
}

/** An older user access / entitlements data structure. */
export interface V2SecurityResponse {
    /** Array of client, version, and bitmap information. */
    bitmapContextTypes: SecurityContext[],
    /** Array of component guids, security version, and bitmap offsets. */
    componentTranslations: SecurityComponent[],
}

const MASKS: (keyof Permissions)[] = [
    'visible',
    'enabled',
    'masked',
    'perform',
    'partialMasked',
    'enabledFull',
];

const ALL: Permissions = {
    visible: true,
    enabled: true,
    perform: true,
    masked: true,
    partialMasked: true,
    enabledFull: true,
};

const NONE: Permissions = {
    visible: false,
    enabled: false,
    perform: false,
    masked: false,
    partialMasked: false,
    enabledFull: false,
};

// previous security versions used this
// token as an indicator of full access;
// we can remove this once ALL consumers
// have migrated from advsec v0
const SENTINEL = 'FX8675309X1337X12345';

function swallow() {
    return Object.create(null);
}

function toPermission<T extends Partial<Permissions>>(permission: T, key: PermissionLevel, index: number): T {
    return set(permission, key, this[index] === Active.ON);
}

function toBitmap(result: SecurityInfo['bitmaps'], context: SecurityContext): SecurityInfo['bitmaps'] {
    return set(result, `${context.caid}@${context.version.substring(1)}`, context.bitmapData);
}

function toComponentMap(result: SecurityInfo['components'], component: SecurityComponent): SecurityInfo['components'] {
    const offset = Number(component.offset);
    const version = Number(component.version.substring(1));
    return set(result, component.guid, [version, offset]);
}

function or(lhs: Permissions, rhs: Permissions): Permissions {
    return MASKS.reduce((out: Permissions, mask: keyof Permissions) => {
        return set(out, mask, out[mask] || rhs[mask]);
    }, lhs);
}

function and(lhs: Permissions, rhs: Permissions): Permissions {
    return MASKS.reduce((out: Permissions, mask: keyof Permissions) => {
        return set(out, mask, out[mask] && rhs[mask]);
    }, lhs);
}

function invokeAsPromise<T = any>(fn: CallableFunction): Promise<T> {
    // sonarlint incorrectly believes Promise.resolve(fn())
    // has the same behavior as invoking fn inside a Promise
    // constructor function; the latter will turn any thrown
    // errors into a rejected promise
    return new Promise<T>((resolve) => { // nosonar
        resolve(fn());
    });
}

/**
 * Creates an API with methods you can invoke to get security information
 * for a given combination of client id (CAID) and secured component GUID.
 *
 * @returns An API with various security methods.
 * @example
 * ```js
 * const userAccess = security.create();
 *
 * userAccess.load(async function getEntitlements() {
 *   // load user access / advanced security for
 *   // a user and/or client(s); be sure to return
 *   // data in the format the security API expects
 *   return userAccess.from.v2(response.data);
 * });
 *
 * // retrieve permissions by caid and component guid:
 * const perms = await userAccess.permissions(caid, guid);
 * console.log(perms); // { visible: ..., enabled: ..., ... }
 *
 * // you can partially apply the permissions function with
 * // a particular client to make repeat calls clearer:
 * const perms = userAccess.permissions(caid);
 * await perms(guid);
 * await perms(anotherGuid);
 *
 * // you can also combine permissions together so that
 * // the resulting privileges are only allowed when all
 * // permissions permit it, OR are allowed when any
 * // permission permits it:
 *
 * const perm1 = await perms(guid1);
 * const perm2 = await perms(guid2);
 * const perm3 = await perms(guid3);
 *
 * const any = userAccess.permissive(perm1, perm2, perm3); // ORs permissions
 * const all = userAccess.restrictive(perm1, perm2, perm3); // ANDs permissions
 *
 * if (any.visible) { ... }
 * else if (all.enabled) { ... }
 * ```
 */
export function create(): SecurityAPI {

    const loaded = signals.manualReset(false);

    let abort = Function.prototype,
        dataPromise: Promise<SecurityInfo> = Promise.resolve(null);

    return {

        ALL: Object.freeze({ ...ALL }),
        NONE: Object.freeze({ ...NONE }),

        async load(loader: SecurityLoader) {

            abort(errors.error('Previous security load aborted.', errors.ignore()));

            const promise = invokeAsPromise(loader);
            dataPromise = promise.catch(swallow);

            await Promise.race([
                promise,
                new Promise((_, reject) => abort = reject),
            ]).finally(loaded.set);

        },

        permissions: curry(async function (caid: Caid, guid: Guid): Promise<Permissions> {

            await loaded.ready();
            const info = await dataPromise;

            if (guid === SENTINEL)
                return ALL;

            if (!has(info.components, guid))
                return NONE;

            const [version, offset] = info.components[guid];
            const key = `${caid}@${version}`;
            if (!has(info.bitmaps, key))
                return NONE;

            const bitmap = info.bitmaps[key];
            const bits = bitmap.slice(offset, offset + info.masks.length);
            if (isEmpty(bits))
                return NONE;

            return info.masks.reduce(toPermission.bind(bits), Object.create(null) as Permissions);

        }, 2),

        permissive(...perms: Permissions[]): Permissions {
            return flatten(perms).concat(NONE).reduce(or);
        },

        restrictive(...perms: Permissions[]): Permissions {
            return flatten(perms).concat(ALL).reduce(and);
        },

        from: {

            v2(v2data: V2SecurityResponse): SecurityInfo {
                return {
                    masks: MASKS,
                    bitmaps: v2data.bitmapContextTypes.reduce(toBitmap, Object.create(null)),
                    components: v2data.componentTranslations.reduce(toComponentMap, Object.create(null)),
                };
            }

        },

    };

}
